import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DateField, SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import CustomerForm from "@/components/customers/CustomerForm";
import { OrderItemsTable } from "@/components/order/OrderItemsTable";
import { OrderChargesSection } from "@/components/order/OrderChargesSection";
import { newLine } from "@/components/order/orderLineUtils";

export default function CreateOrder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editBill = params.get("edit");

  const [customers, setCustomers] = useState([]);
  const [custFormOpen, setCustFormOpen] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState(null);
  const [locked, setLocked] = useState(false);
  const [changeReason, setChangeReason] = useState("");

  const [customerCode, setCustomerCode] = useState("");
  const [shipSame, setShipSame] = useState(true);
  const [shipTo, setShipTo] = useState({ name: "", address: "", state: "", pin: "", gstin: "", contact: "", mobile: "", instructions: "" });
  const [header, setHeader] = useState({ order_date: new Date().toISOString().slice(0, 10), sales_person: "", customer_po_number: "", customer_po_date: "", expected_dispatch_date: "", payment_terms: "", credit_terms: "", delivery_terms: "", transport_mode: "", transporter: "", priority: "normal", special_instructions: "" });
  const [lines, setLines] = useState([newLine()]);
  const [charges, setCharges] = useState([]);
  const [advance, setAdvance] = useState({ amount: "", date: "", mode: "", reference: "", remarks: "" });
  const [busy, setBusy] = useState(false);

  const customer = customers.find((c) => c.code === customerCode);
  const billTo = customer?.billing;

  const loadCustomers = () => api.get("/customers?status=ACTIVE").then((r) => setCustomers(r.data));

  useEffect(() => {
    loadCustomers();
    Promise.all([api.get("/products"), api.get("/settings/terms")]).then(([p, s]) => {
      setProducts(p.data);
      setCompany(s.data);
    });
  }, []);

  useEffect(() => {
    if (!customerCode) return setCreditInfo(null);
    api.get(`/customers/${customerCode}/outstanding`).then((r) => setCreditInfo(r.data)).catch(() => setCreditInfo(null));
    if (!editBill) {
      const c = customers.find((x) => x.code === customerCode);
      if (c) setHeader((h) => ({
        ...h,
        payment_terms: h.payment_terms || c.payment_terms || "",
        credit_terms: h.credit_terms || (c.credit_days ? `${c.credit_days} days` : ""),
        sales_person: h.sales_person || c.sales_person || "",
      }));
    }
  }, [customerCode, customers, editBill]);

  useEffect(() => {
    if (!editBill || !customers.length) return;
    api.get(`/orders/v2/${editBill}`).then((r) => {
      const o = r.data;
      setLocked(!["draft", "submitted"].includes(o.sales_status));
      setCustomerCode(o.customer_code);
      setShipSame(!!o.ship_same_as_bill);
      if (o.ship_to) setShipTo({ name: o.ship_to.name || "", address: o.ship_to.address || "", state: o.ship_to.state || "", pin: o.ship_to.pin || "", gstin: o.ship_to.gstin || "", contact: o.ship_to.contact || "", mobile: o.ship_to.mobile || "", instructions: o.ship_to.instructions || "" });
      setHeader({
        order_date: (o.order_date || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
        sales_person: o.sales_person || "", customer_po_number: o.customer_po_number || "",
        customer_po_date: o.customer_po_date || "", expected_dispatch_date: (o.expected_dispatch_date || "").slice(0, 10) || "",
        payment_terms: o.payment_terms || "", credit_terms: o.credit_terms || "", delivery_terms: o.delivery_terms || "",
        transport_mode: o.transport_mode || "", transporter: o.transporter || "", priority: o.priority || "normal",
        special_instructions: o.special_instructions || "",
      });
      setLines(o.items.map((i) => ({
        sku: i.sku, product_name: i.product_name, hsn: i.hsn || "", uom: i.uom || "PCS",
        quantity: String(i.quantity), rate: String(i.rate), discount_pct: String(i.discount_pct || ""),
        gst_percent: i.gst_percent, pieces_per_carton: i.pieces_per_carton || "", packing_type: i.packing_type || "",
        customer_sku: i.customer_sku || "", customer_po_line: i.customer_po_line || "",
        msp: i.msp, floor_price: i.floor_price, cost_price: i.cost_price, standard_rate: i.standard_rate,
        stock: null, _q: "", _open: false, _uid: crypto.randomUUID(),
      })));
      setCharges((o.charges || []).map((c) => ({ description: c.description, amount: String(c.amount), gst_percent: c.gst_percent ?? "", _uid: crypto.randomUUID() })));
      if (o.advance) setAdvance({ amount: String(o.advance.amount || ""), date: o.advance.date || "", mode: o.advance.mode || "", reference: o.advance.reference || "", remarks: o.advance.remarks || "" });
    }).catch(() => toast.error("Failed to load order"));
  }, [editBill, customers.length]);

  const placeOfSupply = (shipSame ? billTo?.state : shipTo.state) || billTo?.state || "";
  const intraState = (placeOfSupply || "").trim().toLowerCase() === (company?.state || "Tamil Nadu").toLowerCase();

  const calc = useMemo(() => {
    const rows = lines.map((l) => {
      const qty = parseFloat(l.quantity) || 0;
      const rate = parseFloat(l.rate) || 0;
      const gross = qty * rate;
      const disc = gross * ((parseFloat(l.discount_pct) || 0) / 100);
      const taxable = gross - disc;
      const tax = taxable * ((parseFloat(l.gst_percent) || 0) / 100);
      return { ...l, gross, disc, taxable, tax, total: taxable + tax };
    });
    const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
    const chTotal = charges.reduce((a, c) => a + (parseFloat(c.amount) || 0), 0);
    const chTax = charges.reduce((a, c) => a + (parseFloat(c.amount) || 0) * ((parseFloat(c.gst_percent) || 0) / 100), 0);
    const subtotal = sum("taxable") + sum("tax") + chTotal + chTax;
    const total = Math.round(subtotal);
    return {
      rows, gross: sum("gross"), discount: sum("disc"), taxable: sum("taxable"), tax: sum("tax"),
      charges: chTotal, chargesTax: chTax, roundOff: total - subtotal, total,
      received: parseFloat(advance.amount) || 0, balance: total - (parseFloat(advance.amount) || 0),
      totalQty: rows.reduce((a, r) => a + (parseFloat(r.quantity) || 0), 0),
    };
  }, [lines, charges, advance]);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const pickProduct = (i, p) => {
    setLine(i, {
      sku: p.sku, product_name: p.name, hsn: p.hsn || "", uom: p.uom || "PCS",
      rate: String(p.standard_rate ?? ""), gst_percent: p.gst_percent ?? 18,
      pieces_per_carton: p.pieces_per_carton || "", packing_type: p.packing_type || "",
      stock: p.stock, msp: p.msp, floor_price: p.floor_price, cost_price: p.cost_price, standard_rate: p.standard_rate,
      _cust_price: false, _q: "", _open: false,
    });
    if (customerCode) {
      api.get(`/customer-prices/resolve?customer_code=${customerCode}&sku=${p.sku}`)
        .then((r) => { if (r.data?.price != null) setLine(i, { rate: String(r.data.price), _cust_price: true }); })
        .catch(() => {});
    }
  };

  const save = async (action) => {
    if (!customerCode) return toast.error("Select a customer");
    if (!lines.some((l) => l.sku && parseFloat(l.quantity) > 0)) return toast.error("Add at least one SKU with quantity");
    if (locked && !changeReason.trim()) return toast.error("Change reason is required for approved orders");
    setBusy(true);
    const payload = {
      customer_code: customerCode,
      ...header,
      order_date: header.order_date || null,
      ship_same_as_bill: shipSame,
      ship_to: shipSame ? null : shipTo,
      items: lines.filter((l) => l.sku).map((l) => ({
        sku: l.sku, quantity: parseInt(l.quantity) || 0, rate: parseFloat(l.rate) || 0,
        discount_pct: parseFloat(l.discount_pct) || 0, gst_percent: parseFloat(l.gst_percent) || 0,
        hsn: l.hsn || null, uom: l.uom || null, customer_po_line: l.customer_po_line || null,
        customer_sku: l.customer_sku || null, pieces_per_carton: parseInt(l.pieces_per_carton) || null,
        packing_type: l.packing_type || null,
      })),
      charges: charges.filter((c) => c.description && c.amount).map((c) => ({ description: c.description, amount: parseFloat(c.amount), gst_percent: c.gst_percent === "" ? null : parseFloat(c.gst_percent) })),
      advance: advance.amount ? { ...advance, amount: parseFloat(advance.amount) } : null,
      action,
    };
    try {
      let billNo = editBill;
      if (editBill) {
        await api.patch(`/orders/v2/${editBill}${changeReason ? `?change_reason=${encodeURIComponent(changeReason)}` : ""}`, payload);
        toast.success("Order updated");
      } else {
        const res = await api.post("/orders/v2", payload);
        billNo = res.data.bill_no;
        toast.success(`Order ${billNo} ${action === "draft" ? "saved as draft" : "submitted for approval"}`);
      }
      navigate(`/orders/${billNo}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save order");
    } finally {
      setBusy(false);
    }
  };

  const H = (label, node) => <div><Label className="text-xs">{label}</Label>{node}</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-5 pb-10" data-testid="create-order-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight uppercase">{editBill ? `Edit ${editBill}` : "Create Bill / Order"}</h1>
        {locked && <span className="text-[10px] font-mono uppercase tracking-widest text-signal-amber border border-signal-amber/40 rounded px-2 py-1">Approved order — change requires reason</span>}
      </div>

      {/* Section 1: Order info + customer */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="section-order-info">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Section 1 — Order & Customer</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {H("Customer *", (
            <div className="flex gap-1">
              <Select value={customerCode} onValueChange={setCustomerCode}>
                <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" variant="outline" className="shrink-0 px-2" title="Add New Customer" onClick={() => setCustFormOpen(true)} data-testid="add-new-customer-button"><Plus className="w-4 h-4" /></Button>
            </div>
          ))}
          <DateField label="Order Date" testId="order-date-input" value={header.order_date} onChange={(v) => setHeader({ ...header, order_date: v })} />
          {H("Sales Person", <Input data-testid="order-salesperson-input" value={header.sales_person} onChange={(e) => setHeader({ ...header, sales_person: e.target.value })} placeholder={user?.name} />)}
          {H("Priority", (
            <Select value={header.priority} onValueChange={(v) => setHeader({ ...header, priority: v })}>
              <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
            </Select>
          ))}
          {H("Customer PO Number", <Input data-testid="order-po-input" value={header.customer_po_number} onChange={(e) => setHeader({ ...header, customer_po_number: e.target.value })} />)}
          <DateField label="Customer PO Date" testId="order-po-date-input" value={header.customer_po_date} onChange={(v) => setHeader({ ...header, customer_po_date: v })} />
          <DateField label="Expected Dispatch" testId="order-expected-dispatch-input" value={header.expected_dispatch_date} onChange={(v) => setHeader({ ...header, expected_dispatch_date: v })} />
          {H("Transport Mode", <Input data-testid="order-transport-mode-input" value={header.transport_mode} onChange={(e) => setHeader({ ...header, transport_mode: e.target.value })} placeholder="Road / Air" />)}
          {H("Payment Terms", <Input data-testid="order-payment-terms-input" value={header.payment_terms} onChange={(e) => setHeader({ ...header, payment_terms: e.target.value })} placeholder="30 days" />)}
          {H("Credit Terms", <Input data-testid="order-credit-terms-input" value={header.credit_terms} onChange={(e) => setHeader({ ...header, credit_terms: e.target.value })} />)}
          {H("Delivery Terms", <Input data-testid="order-delivery-terms-input" value={header.delivery_terms} onChange={(e) => setHeader({ ...header, delivery_terms: e.target.value })} placeholder="Ex-factory" />)}
          {H("Transporter", <Input data-testid="order-transporter-input" value={header.transporter} onChange={(e) => setHeader({ ...header, transporter: e.target.value })} />)}
        </div>
        {H("Special Instructions", <Textarea data-testid="order-instructions-input" value={header.special_instructions} onChange={(e) => setHeader({ ...header, special_instructions: e.target.value })} />)}
        {creditInfo?.credit_limit > 0 && (
          <div className={`flex items-center gap-2 text-xs rounded-md px-3 py-2 border ${creditInfo.total_outstanding + calc.total > creditInfo.credit_limit ? "border-signal-amber/50 bg-signal-amber/10 text-signal-amber" : "border-border text-muted-foreground"}`} data-testid="credit-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              Credit limit ₹{creditInfo.credit_limit.toLocaleString("en-IN")} · Outstanding ₹{creditInfo.total_outstanding.toLocaleString("en-IN")} · Available ₹{(creditInfo.available_credit ?? 0).toLocaleString("en-IN")}
              {creditInfo.total_outstanding + calc.total > creditInfo.credit_limit && <strong> — WARNING: this order exceeds the customer's credit limit</strong>}
            </span>
          </div>
        )}
      </div>

      {/* Section 2: Bill to / Ship to */}
      <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="section-billto-shipto">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Section 2 — Bill To / Ship To</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="w-4 h-4 accent-primary" data-testid="same-as-billto-check" checked={shipSame} onChange={(e) => setShipSame(e.target.checked)} />
            Same as Bill To
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-border rounded-md p-3 text-sm space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Bill To</p>
            {billTo ? (
              <>
                <p className="font-semibold">{customer.name}</p>
                <p className="text-muted-foreground">{billTo.address}, {billTo.state} — {billTo.pin}</p>
                <p className="metric text-xs">GSTIN: {billTo.gstin} · PAN: {billTo.pan}</p>
                <p className="text-xs text-muted-foreground">{billTo.contact} · {billTo.mobile} · {billTo.email}</p>
                <p className="text-xs">Place of Supply: <span className="metric text-primary">{placeOfSupply}</span> → <span className="font-semibold">{intraState ? "CGST + SGST" : "IGST"}</span></p>
              </>
            ) : <p className="text-muted-foreground">Select a customer above</p>}
          </div>
          <div className="border border-border rounded-md p-3 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Ship To</p>
            {shipSame ? (
              <p className="text-sm text-muted-foreground">Same as Bill To</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {(customer?.ship_tos || []).length > 0 && (
                  <div className="col-span-2">
                    <Select onValueChange={(v) => { const s = customer.ship_tos[parseInt(v)]; setShipTo({ name: s.name || "", address: s.address || "", state: s.state || "", pin: s.pin || "", gstin: s.gstin || "", contact: s.contact || "", mobile: s.mobile || "", instructions: s.instructions || "" }); }}>
                      <SelectTrigger data-testid="shipto-preset-select"><SelectValue placeholder="Pick saved delivery location" /></SelectTrigger>
                      <SelectContent>{customer.ship_tos.map((s, i) => <SelectItem key={`${s.name || "ship"}-${s.pin || i}`} value={String(i)}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <Input data-testid="shipto-name-input" placeholder="Ship-to name" value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} />
                <Input data-testid="shipto-contact-input" placeholder="Contact person" value={shipTo.contact} onChange={(e) => setShipTo({ ...shipTo, contact: e.target.value })} />
                <div className="col-span-2"><Input data-testid="shipto-address-input" placeholder="Address" value={shipTo.address} onChange={(e) => setShipTo({ ...shipTo, address: e.target.value })} /></div>
                <Input data-testid="shipto-state-input" placeholder="State" value={shipTo.state} onChange={(e) => setShipTo({ ...shipTo, state: e.target.value })} />
                <Input data-testid="shipto-pin-input" placeholder="PIN" value={shipTo.pin} onChange={(e) => setShipTo({ ...shipTo, pin: e.target.value })} />
                <Input data-testid="shipto-gstin-input" placeholder="GSTIN" value={shipTo.gstin} onChange={(e) => setShipTo({ ...shipTo, gstin: e.target.value })} />
                <Input data-testid="shipto-mobile-input" placeholder="Mobile" value={shipTo.mobile} onChange={(e) => setShipTo({ ...shipTo, mobile: e.target.value })} />
                <div className="col-span-2"><Input data-testid="shipto-instructions-input" placeholder="Delivery instructions" value={shipTo.instructions} onChange={(e) => setShipTo({ ...shipTo, instructions: e.target.value })} /></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <OrderItemsTable lines={lines} calc={calc} products={products} intraState={intraState}
        setLines={setLines} setLine={setLine} pickProduct={pickProduct} />

      <OrderChargesSection charges={charges} setCharges={setCharges} advance={advance} setAdvance={setAdvance}
        calc={calc} intraState={intraState} />

      {locked && (
        <div className="bg-card border border-signal-amber/40 rounded-lg p-4">
          <Label>Change reason (required — recorded in version history)</Label>
          <Input data-testid="change-reason-input" value={changeReason} onChange={(e) => setChangeReason(e.target.value)} placeholder="e.g. Customer revised PO" />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button variant="secondary" disabled={busy} onClick={() => save("draft")} data-testid="save-draft-button" className="h-12 px-6">SAVE DRAFT</Button>
        <Button disabled={busy} onClick={() => save("submit")} data-testid="submit-approval-button" className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-bold">
          {editBill ? "SAVE CHANGES" : "SUBMIT FOR APPROVAL"}
        </Button>
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="cancel-order-button" className="h-12">CANCEL</Button>
      </div>

      <CustomerForm open={custFormOpen} onOpenChange={setCustFormOpen}
        onSaved={(c) => { loadCustomers(); setCustomerCode(c.code); toast.success(`${c.name} selected`); }} />
    </div>
  );
}
